import { Erc4626ReadAbi, TotalSupplyAbi } from '../abis'
import { AvailableWithdrawLimitAbi } from '../abis/yearnStrategy'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * A share balance would clamp the limit read to that owner's holdings,
 * and protocol state (an open borrow, a deposit commitment) can zero
 * it. `0xdead` has neither, so the read returns the global figure.
 */
const NEUTRAL_PROBE = '0x000000000000000000000000000000000000dead'

/**
 * ERC-4626 whose exit is capped by the strategy's own
 * `availableWithdrawLimit` — four calls:
 *   1. `totalAssets()`
 *   2. `totalSupply()`
 *   3. `convertToAssets(10**shareDecimals)`
 *   4. `availableWithdrawLimit(0xdead)`
 *
 * The sibling of [erc4626Idle.ts](./erc4626Idle.ts) for vaults whose
 * instant-exit cap is **not** a token balance anywhere. 3Jane's USD3
 * (a Yearn v3 strategy) aggregates idle USDC with what its
 * MorphoCredit market can pay out of Aave-wrapped USDC, and zeroes the
 * whole limit under unrealized losses — a computed figure only the
 * strategy itself can produce. Pair with
 * `withdrawalMode: 'instant-capped'`.
 *
 * The probe address must carry no protocol state (see
 * [the ABI note](../abis/yearnStrategy.ts)); `0xdead` holds no shares,
 * has no borrow and no deposit commitment, so the read is the global
 * liquidity. A failed limit read leaves the capacity unset, which
 * `fetchPublic` renders as `liquidity: 0` — conservative rather than
 * optimistic, and it never drops an otherwise-healthy entry.
 */
export const readerErc4626WithdrawLimit = (
  entry: SavingsRegistryEntry,
): SavingsReader => {
  const shareUnit = 10n ** BigInt(entry.decimals)
  const underlyingUnit =
    10n ** BigInt(entry.underlyingDecimals ?? entry.decimals)
  return {
    calls: [
      { address: entry.address, name: 'totalAssets', params: [] },
      { address: entry.address, name: 'totalSupply', params: [] },
      {
        address: entry.address,
        name: 'convertToAssets',
        params: [shareUnit],
      },
      {
        address: entry.address,
        name: 'availableWithdrawLimit',
        params: [NEUTRAL_PROBE],
      },
    ],
    abis: [
      Erc4626ReadAbi,
      TotalSupplyAbi,
      Erc4626ReadAbi,
      AvailableWithdrawLimitAbi,
    ],
    parse: ([assets, supply, rate, withdrawLimit]) => {
      const totalAssets = toBigInt(assets)
      const totalSupply = toBigInt(supply)
      const convertToAssetsRaw = toBigInt(rate)
      if (
        totalAssets === undefined ||
        totalSupply === undefined ||
        convertToAssetsRaw === undefined
      ) {
        return undefined
      }

      // Enrichment only — must not drop an otherwise-healthy entry.
      const capacity = toBigInt(withdrawLimit)

      return {
        totalAssets,
        totalSupply,
        exchangeRate: (convertToAssetsRaw * ONE_E18) / underlyingUnit,
        ...(capacity !== undefined
          ? {
              instantRedeemCapacity: capacity,
              instantRedeemEnabled: true,
              withdrawFeeBps: 0,
            }
          : {}),
      }
    },
  }
}
